import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Collapse from "@mui/material/Collapse";
import { IPOOrderHistoryResponse } from "./helpers";
import { replaceCharsForSecurityName, toINR } from "../../../Helpers/functions";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import copy from "copy-to-clipboard";
import ArrowDownwardRounded from "@mui/icons-material/ArrowDownwardRounded";
import BSELogo from "../../../images/BSE-Logo_Color.png";
import CheckCircleTwoToneIcon from "@mui/icons-material/CheckCircleTwoTone";
import RotateRightRoundedIcon from "@mui/icons-material/RotateRightRounded";
import Tooltip from "@mui/material/Tooltip";
import InfoTwoToneIcon from "@mui/icons-material/InfoTwoTone";

const getColorForAllotmentStatus = (value: string) => {
  switch (value) {
    case "Pending Allotment":
      return { color: "warning" };
    case "Successful Allotment":
      return { color: "success" };
    case "Partial Allotment":
      return { color: "success" };
    case "Unsuccessful Allotment":
      return { color: "secondary" };
    case "Cancelled":
      return { color: "error" };
    default:
      return { color: "primary" };
  }
};

const getPaymentStatus = (code: number) => {
  switch (code) {
    case 1:
      return {
        status: "Bank Response Pending",
        color: "warning",
      };
    case 10:
      return {
        status: "Request accepted by Bank",
        color: "warning",
      };
    case 11:
      return {
        status: "Request rejected by Bank due to invalid UPI handle",
        color: "error",
      };
    case 12:
      return {
        status: "Request rejected by Bank due to other reasons",
        color: "error",
      };
    case 21:
      return {
        status: "Request rejected by Client Bank",
        color: "error",
      };
    case 31:
      return {
        status: "Request rejected by Client",
        color: "error",
      };
    case 100:
      return {
        status: "Request accepted by Client. Payment successful.",
        color: "success",
      };
    case 110:
      return {
        status: "Request processed successfully",
        color: "success",
      };
    default:
      return {
        status: "Bank Response Pending",
        color: "warning",
      };
  }
};

const getDPStatus = (code: string) => {
  switch (code) {
    case "P":
      return {
        status: "Pending",
        color: "warning",
      };
    case "S":
      return {
        status: "Success",
        color: "success",
      };
    case "R":
      return {
        status: "Rejected",
        color: "secondary",
      };
    default:
      return {
        status: "Pending",
        color: "warning",
      };
  }
};

const IpoOrderInfo = ({
  item,
  index,
  collapseIndex,
  setCollapseIndex,
}: {
  item: IPOOrderHistoryResponse;
  index: number;
  collapseIndex: number;
  setCollapseIndex: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const statuses = useMemo(() => {
    const arr = [
      { title: "IPO", color: "primary.main" },
      {
        title: item.allotmentstatus,
        color: `${getColorForAllotmentStatus(item.allotmentstatus).color}.main`,
      },
    ];
    if (item.allotmentstatus !== "Cancelled") {
      arr.push(
        {
          title: `Application Number:${item.applicationno || "TBA"}`,
          color: "primary.main",
        },
        {
          title: item.bids[0]?.bidid?.length > 1 ? "Processed" : "Processing",
          color:
            item.bids[0]?.bidid?.length > 1 ? "success.main" : "warning.main",
        }
      );
    }
    return arr;
  }, [item]);

  const basicInfo = useMemo(() => {
    return [
      {
        label: "BSE Bid ID",
        value: item.bids[0]?.bidid?.length > 1 ? item.bids[0].bidid : "TBA",
      },
      {
        label: "Series",
        value: item.bids[0].series,
      },
      {
        label: "Tenure",
        value: item.ipo_quote_tenure,
      },
      {
        label: "Quantity",
        value: Number(item.bids[0].quantity).toFixed(0),
      },
      {
        label: "Amount",
        value: toINR(item.chequeamount, 4),
      },
      {
        label: "PAN",
        value: item.panno,
      },
      {
        label: "UPI Handle",
        value: item.accountnumber_upiid,
      },
      {
        label: "Depository Information",
        value:
          item.depository === "NSDL"
            ? `${item.dpid}${item.clientbenfid}`
            : item.clientbenfid,
      },
    ];
  }, [item]);

  const bseStatuses = useMemo(() => {
    return [
      {
        title: "DP Status",
        value: item.dpflag === "S",
        info: `Verification ${getDPStatus(item.dpflag).status} ${
          item.dpremark ? "- " + item.dpremark : ""
        }`,
      },
      {
        title: "Amount Status",
        value: [100, 110].includes(Number(item.sponsorbankpaystatus)),
        info: `${getPaymentStatus(Number(item.sponsorbankpaystatus)).status} ${
          item.rejectreason ? "- " + item.rejectreason : ""
        }`,
      },
      {
        title: "Allotment",
        value: ["Successful Allotment", "Partial Allotment"].includes(
          item.allotmentstatus
        ),
        info: item.allotmentstatus,
      },
    ];
  }, [item]);

  return (
    <Box
      sx={{
        mt: 5,
        borderRadius: "12px",
        border: "1px solid #9397A4",
        p: 2,
        position: "relative",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          borderRadius: [1, 2],
          overflow: "hidden",
          top: -12,
          left: ["50%", 25],
          transform: ["translateX(-50%)", "translateX(0)"],
          display: "flex",
          alignItems: "stretch",
        }}
      >
        {statuses.map((status, i) => (
          <Box
            key={i}
            sx={{
              bgcolor: status.color,
              display: "flex",
              alignItems: "center",
              py: 0.5,
              px: [1, 2],
            }}
          >
            <Typography
              sx={{
                color: "#FFFFFF",
                fontWeight: 600,
                fontSize: [7, 10],
                whiteSpace: "nowrap",
              }}
            >
              {status.title}
            </Typography>
          </Box>
        ))}
      </Box>
      <Grid container spacing={2} mb={2} sx={{ mt: "-5px" }}>
        <Grid item xs={12} sm={true}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "flex-start",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #DFE0E4",
                borderRadius: "50%",
                bgcolor: "#FFF",
              }}
            >
              <Box
                component={"img"}
                src={
                  item.image
                    ? `data:image/png;base64,${item.image}`
                    : "/favicon.png"
                }
                width={40}
                height={40}
                alt="Bond Logo"
                style={{
                  objectFit: "contain",
                  borderRadius: "50%",
                  padding: "5px",
                }}
              />
            </Box>
            <Box sx={{ ml: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: [11, 14],
                    fontWeight: 800,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {item.ipo_security_name}
                </Typography>
                {item.isin && (
                  <IconButton
                    href={`https://smest.in/bond-details/${item.ipo_security_name.replace(
                      /[% ]/g,
                      (m) => replaceCharsForSecurityName[m]
                    )}/${item.isin}`}
                    target="_blank"
                    sx={{ ml: 0.5, pt: 0.5 }}
                  >
                    <RocketLaunchRoundedIcon />
                  </IconButton>
                )}
              </Box>
              {item.isin && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: [10, 12],
                      fontWeight: 700,
                      color: "info.main",
                    }}
                  >
                    {item.isin}
                  </Typography>
                  <IconButton
                    sx={{
                      p: 0,
                      ml: 0.5,
                    }}
                    onClick={() => copy(item.isin)}
                  >
                    <ContentCopyRoundedIcon />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} sm={"auto"}>
          <Box
            sx={{
              borderRadius: [2, 3],
              overflow: "hidden",
              width: [100, 150],
              height: [30, 50],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#FFFFFF",
            }}
          >
            <video
              src={`/videos/${
                item.category.startsWith("3") ? "hni" : "retail"
              }.mp4`}
              autoPlay
              loop
              muted
              playsInline
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          </Box>
        </Grid>
      </Grid>
      <Grid container spacing={[0.5, 2]}>
        {basicInfo.map((info, i) => (
          <Grid item xs={6} sm={"auto"} md={2} key={i}>
            <Typography
              sx={{
                fontSize: [9, 10],
                fontWeight: 600,
                opacity: 0.5,
                whiteSpace: "nowrap",
              }}
            >
              {info.label}
            </Typography>
            <Typography
              sx={{
                fontSize: [10, 13],
                fontWeight: 700,
              }}
            >
              {info.value}
            </Typography>
          </Grid>
        ))}
        {item.allotmentstatus !== "Cancelled" && (
          <Grid
            item
            xs={12}
            sm={"auto"}
            md={true}
            container
            alignItems="flex-end"
            justifyContent="flex-end"
          >
            <ArrowDownwardRounded
              onClick={() =>
                collapseIndex === index
                  ? setCollapseIndex(-1)
                  : setCollapseIndex(index)
              }
              style={{
                transition: "transform 200ms",
                transform:
                  collapseIndex === index ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </Grid>
        )}
      </Grid>
      <Collapse in={collapseIndex === index}>
        <Box
          sx={{
            fontWeight: 600,
            mt: 3,
            mb: ["Successful Allotment", "Partial Allotment"].includes(
              item.allotmentstatus
            )
              ? [2, 0]
              : 2,
            borderTop: "2px solid #BEC1C9",
            borderBottom: "none !important",
            borderLeft: "none !important",
            borderRight: "none !important",
            borderStyle: "dashed",
            pt: 2,
            display: "flex",
            alignItems: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <a href={"https://www.bseindia.com"} target="_blank">
            <Box
              component={"img"}
              src={BSELogo}
              alt="BSE Logo"
              width={50}
              height={30}
              style={{
                marginBottom: -3,
              }}
            />
          </a>
          <Box component={"span"} sx={{ mx: 0.5, opacity: 0.5 }}>
            |
          </Box>
          <Typography
            variant="h6"
            noWrap
            sx={{
              fontSize: 13,
              fontWeight: 600,
              opacity: 0.7,
            }}
          >
            Exchange Status
          </Typography>
        </Box>
        <Grid container spacing={[0.5, 2]} mb={1} alignItems="end">
          {bseStatuses.map((status, i) => (
            <Grid
              item
              xs={6}
              sm={3}
              md={2}
              key={i}
              container
              alignItems={"center"}
              flexWrap="nowrap"
            >
              {status.value ? (
                <CheckCircleTwoToneIcon
                  style={{
                    marginRight: 5,
                    color: "#53BB53",
                  }}
                />
              ) : (
                <RotateRightRoundedIcon
                  style={{
                    marginRight: 5,
                    color: "#FF0000",
                  }}
                />
              )}
              <Typography
                sx={{
                  fontSize: [9, 13],
                  fontWeight: 600,
                }}
              >
                {status.title}
              </Typography>
              <Tooltip
                enterTouchDelay={0}
                followCursor
                placement="top"
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      width: 150,
                      fontSize: 12,
                      fontWeight: 600,
                      borderRadius: 1.5,
                      px: 1,
                    }}
                  >
                    {status.info}
                  </Typography>
                }
              >
                <InfoTwoToneIcon
                  style={{
                    marginLeft: 5,
                    cursor: "text",
                  }}
                />
              </Tooltip>
            </Grid>
          ))}
          {["Successful Allotment", "Partial Allotment"].includes(
            item.allotmentstatus
          ) && (
            <Grid item xs={6} sm="auto">
              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: 800,
                  background: "linear-gradient(to right, #add100, #7b920a)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                  textShadow: "0px 2px 8px rgba(123, 146, 10, 0.3)",
                }}
              >
                {item.allotmentstatus === "Partial Allotment"
                  ? Number(item.final_units).toFixed(0)
                  : Number(item.bids[0].quantity).toFixed(0)}
              </Typography>
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 600,
                  opacity: 0.5,
                  whiteSpace: "nowrap",
                }}
              >
                Allotted Quantity
              </Typography>
            </Grid>
          )}
        </Grid>
      </Collapse>
    </Box>
  );
};

export default IpoOrderInfo;
